import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Button, 
  Descriptions, 
  List,
  Badge,
  Alert,
  Modal,
  message
} from 'antd';
import { 
  UserOutlined, 
  BankOutlined, 
  SafetyOutlined,
  WalletOutlined,
  TransactionOutlined,

  RocketOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { useAccount } from '../../context/AccountContext';
import { formatAmount, formatDateTime } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { transactionApi } from '../../services/api';

const { Title, Text } = Typography;

interface RecentTransaction {
  id: string;
  type: string;
  amount: string;
  counterpart: string;
  status: 'success' | 'pending' | 'failed';
  time: string;
}

const AccountOverview: React.FC = () => {
  const { accountStatus } = useAccount();
  const navigate = useNavigate();

  
  const [recentTransactions] = useState<RecentTransaction[]>([
    {
      id: '1',
      type: '支付',
      amount: '15000.00',
      counterpart: '杭州云数据服务有限公司',
      status: 'success',
      time: '2025-09-21T10:30:00Z'
    },
    {
      id: '2',
      type: '充值',
      amount: '50000.00',
      counterpart: '交通银行',
      status: 'success',
      time: '2025-09-21T09:15:00Z'
    },
    {
      id: '3',
      type: '提现',
      amount: '8000.00',
      counterpart: '交通银行',
      status: 'pending',
      time: '2025-09-21T08:45:00Z'
    }
  ]);


  // 重置交易密码处理函数
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResetPassword = async () => {
    try {
      // 检查必要的账户信息
      if (!accountStatus.ledgerInfo?.rgsn_book_id || !accountStatus.memberInfo?.mshp_cust_id) {
        message.error('账户信息不完整，无法重置交易密码');
        return;
      }

      // 调用重置交易密码API
      await transactionApi.resetPassword({
        ledgerId: accountStatus.ledgerInfo.rgsn_book_id,
        memberId: accountStatus.memberInfo.mshp_cust_id
      });
      
      message.success('交易密码重置请求已发送，请查看小程序完成重置');
      Modal.destroyAll(); // 关闭所有模态框
    } catch (error) {
      console.error('重置交易密码失败:', error);
      message.error('重置交易密码失败，请稍后重试');
    } finally {
    }
  };

  // 状态图标映射
  const statusIconMap = {
    success: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
    pending: <ClockCircleOutlined style={{ color: '#faad14' }} />,
    failed: <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
  };

  // 交易状态映射
  const statusTextMap = {
    success: '成功',
    pending: '处理中',
    failed: '失败'
  };

  const statusColorMap = {
    success: 'success',
    pending: 'processing',
    failed: 'error'
  } as const;

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          账户概览
        </Title>
        <Button 
          type="primary"
          size="large"
          icon={<RocketOutlined />} 
          onClick={() => window.open('/#/account-open', '_blank')}
          style={{ fontWeight: 600 }}
        >
          立即开户
        </Button>
      </div>

      {/* 账户余额和快捷操作 */}
      <Card style={{ marginBottom: 24 }}>
        <Row align="middle">
          <Col flex="auto">
            <div>
              <Text type="secondary" style={{ fontSize: '14px' }}>可用余额</Text>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginTop: 4 }}>
                ¥{formatAmount(accountStatus.ledgerInfo?.ava_bal || '0.00')}
              </div>
              <Space style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  账户余额：¥{formatAmount(accountStatus.balance)}
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  冻结金额：¥{formatAmount('0.00')} {/* Placeholder as frozen amount is not in Ledger type */}
                </Text>
              </Space>
            </div>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<WalletOutlined />}
                size="large"
                onClick={() => navigate('/account/recharge')}
              >
                充值
              </Button>
              <Button 
                icon={<BankOutlined />}
                size="large"
                onClick={() => navigate('/account/withdraw')}
              >
                提现
              </Button>
              <Button 
                icon={<CreditCardOutlined />}
                size="large"
                onClick={() => navigate('/account/cards')}
              >
                银行卡管理
              </Button>
              <Button 
                icon={<TransactionOutlined />}
                size="large"
                onClick={() => navigate('/transactions')}
              >
                交易记录
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 详细信息和最近交易 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <SafetyOutlined />
                账户信息
              </Space>
            }
            extra={
              <Button 
                type="primary" 
                icon={<SafetyOutlined />}
                size="middle"
                onClick={() => {
                  Modal.confirm({
                    title: '重置交易密码',
                    width: 500,
                    icon: <SafetyOutlined style={{ color: '#1890ff' }} />,
                    content: (
                      <div style={{ textAlign: 'center', padding: '12px 0' }}>
                        <div style={{ marginBottom: 12 }}>
                          <SafetyOutlined style={{ fontSize: 40, color: '#1890ff' }} />
                        </div>
                        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
                          请在交通银行小程序中完成交易密码重置
                        </Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          打开微信 → 扫描交通银行小程序二维码 → 小程序中完成交易密码重置
                        </Text>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: 12, 
                          borderRadius: 8,
                          marginTop: 16,
                          border: '1px solid #e9ecef'
                        }}>
                          <div style={{ fontSize: 14, color: '#495057', marginBottom: 8, fontWeight: 500 }}>
                            小程序二维码占位
                          </div>
                          <div style={{ 
                            width: 140, 
                            height: 140, 
                            backgroundColor: '#ffffff', 
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #dee2e6',
                            borderRadius: 4
                          }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>二维码</Text>
                          </div>
                        </div>
                      </div>
                    ),
                    okText: '我知道了',
                    okButtonProps: { type: 'primary' },
                    cancelButtonProps: { style: { display: 'none' } },
                    onOk: () => Modal.destroyAll()
                  });
                }}
              >
                重置交易密码
              </Button>
            }
          >
            {accountStatus.memberInfo ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="企业名称">
                  {accountStatus.memberInfo.cmpy_nme}
                </Descriptions.Item>
                <Descriptions.Item label="组织机构代码">
                  {accountStatus.memberInfo.org_inst_cd}
                </Descriptions.Item>
                <Descriptions.Item label="法定代表人">
                  {accountStatus.memberInfo.lgl_rprs_nme}
                </Descriptions.Item>
                <Descriptions.Item label="授权代理人">
                  {accountStatus.memberInfo.ath_agnt_nme}
                </Descriptions.Item>
                <Descriptions.Item label="开户时间">
                  {accountStatus.ledgerInfo?.createTime ? 
                    formatDateTime(accountStatus.ledgerInfo.createTime) : '-'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                message="暂无账户信息"
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <TransactionOutlined />
                最近交易
              </Space>
            }
            extra={
              <Button 
                type="link"
                icon={<ArrowRightOutlined />}
                size="small"
                onClick={() => navigate('/transactions')}
              >
                查看全部
              </Button>
            }
          >
            <List
              dataSource={recentTransactions}
              renderItem={(transaction) => (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Row align="middle">
                      <Col flex="auto">
                        <Space>
                          {statusIconMap[transaction.status]}
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {transaction.type} - {transaction.counterpart}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {formatDateTime(transaction.time)}
                            </Text>
                          </div>
                        </Space>
                      </Col>
                      <Col>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ 
                            fontWeight: 'bold', 
                            color: transaction.type === '充值' ? '#52c41a' : '#1890ff',
                            fontSize: '16px'
                          }}>
                            {transaction.type === '充值' ? '+' : '-'}¥{formatAmount(transaction.amount)}
                          </div>
                          <Badge 
                            status={statusColorMap[transaction.status]} 
                            text={statusTextMap[transaction.status]}
                            style={{ fontSize: '12px' }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountOverview;
